/**
 * 认证态（spec 06 §4：JWT access 2h / refresh 7d）。
 *
 * 存储取舍：access token 只放内存，refresh token 落 localStorage 以便刷新页面后免登录。
 * 更稳的做法是 refresh 走 httpOnly + SameSite Cookie，由服务端下发——那要后端配合，
 * 已在 `.xj-cursor` 的联调清单里提出。在切过去之前，这里是已知的 XSS 暴露面，
 * 靠 CSP + 不渲染任何未转义 HTML 兜。
 */
import { create } from 'zustand';
import { api, ApiError, configureAuth } from '@/api/client';
import type { Me } from '@/api/types.contract';
import { syncServerPermissions } from '@/lib/permission';

const REFRESH_KEY = 'e2e_console_refresh';

interface LoginResult {
  access_token: string;
  refresh_token: string;
  expires_in_seconds: number;
}

/** 自助注册入参（POST /auth/register，服务端 console/register.go registerRequest）。 */
export interface RegisterInput {
  phone: string;
  code: string;
  company_name: string;
  nickname?: string;
  password?: string;
}

/** 注册结果：除登录票外只多一个电话平台子账户的开通状态。 */
export interface RegisterResult extends LoginResult {
  tenant_id: string;
  /** ready = 电话平台子账户已开好；pending = 正在开通（后台重试中） */
  upstream_status: 'ready' | 'pending';
}

interface AuthState {
  accessToken: string | null;
  me: Me | null;
  /** 首屏尚未确定登录态时为 true，用于避免登录页闪一下 */
  bootstrapping: boolean;
  /**
   * 被服务端踢回登录页的原因（如「您在该企业的账号已停用」）。登录页显示一次后清掉；
   * 普通的会话过期没有原因，登录页什么都不提示。
   */
  kickReason: string | null;
  login: (phone: string, code: string) => Promise<void>;
  loginWithPassword: (phone: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  /** 本地清掉登录态、不再调服务端登出：服务端已经吊销了（改密、转让企业主之后）。 */
  dropSession: () => void;
  /** 重拉 /auth/me，本人资料变了（改昵称）壳上立即刷新。 */
  refreshMe: () => Promise<void>;
  bootstrap: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
}

function readRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeRefresh(token: string | null): void {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    // 隐私模式下 localStorage 可能不可写；退化成「本次会话有效」
  }
}

export const useAuth = create<AuthState>((set, get) => ({
  accessToken: null,
  me: null,
  bootstrapping: true,
  kickReason: null,

  login: async (phone, code) => {
    const res = await api.post<LoginResult>('/auth/login', {
      body: { phone, code },
      refreshOn401: false,
    });
    await adoptSession(res, set);
  },

  loginWithPassword: async (phone, password) => {
    const res = await api.post<LoginResult>('/auth/login/password', {
      body: { phone, password },
      refreshOn401: false,
    });
    await adoptSession(res, set);
  },

  register: async (input) => {
    // 服务端 DisallowUnknownFields：只发它认的键，可选项为空就不带。
    const body: Record<string, string> = {
      phone: input.phone,
      code: input.code,
      company_name: input.company_name,
    };
    if (input.nickname) body.nickname = input.nickname;
    if (input.password) body.password = input.password;
    const res = await api.post<RegisterResult>('/auth/register', {
      body,
      refreshOn401: false,
    });
    await adoptSession(res, set);
    return res;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // 服务端登出失败不该把用户困在里面，本地照样清
    }
    writeRefresh(null);
    set({ accessToken: null, me: null });
  },

  dropSession: () => {
    writeRefresh(null);
    set({ accessToken: null, me: null });
  },

  refreshMe: async () => {
    const me = await fetchMe();
    set({ me });
  },

  bootstrap: async () => {
    if (!readRefresh()) {
      set({ bootstrapping: false });
      return;
    }
    const token = await refreshAccessToken();
    if (!token) {
      set({ bootstrapping: false });
      return;
    }
    try {
      const me = await fetchMe();
      set({ me, bootstrapping: false });
    } catch {
      writeRefresh(null);
      set({ accessToken: null, me: null, bootstrapping: false });
    }
  },

  switchTenant: async (tenantId) => {
    const res = await api.post<LoginResult>('/auth/switch-tenant', {
      body: { tenant_id: tenantId },
    });
    writeRefresh(res.refresh_token);
    set({ accessToken: res.access_token });
    const me = await fetchMe();
    set({ me });
    void get();
  },
}));

/* ========== /auth/me 边界归一 ==========
 * 这个响应是整个壳的地基：AppShell 直接读 me.tenant.name、每个页面按 me.roles
 * 判能力。服务端一旦少给一层对象，读 `me.tenant.name` 就抛 TypeError，
 * 而它在根布局里，抛出去等于整个控制台白屏（2026-09 线上真炸过：服务端
 * 当时返回的是扁平的 tenant_name，前端契约要的是嵌套 tenant 对象）。
 * 契约本身由服务端修正，这里只保证「契约被违反时降级、不白屏」。
 */

function field(o: unknown, k: string): unknown {
  return typeof o === 'object' && o !== null ? (o as Record<string, unknown>)[k] : undefined;
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asBool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined;
}

function normalizeMe(raw: unknown): Me {
  const user = field(raw, 'user');
  const tenant = field(raw, 'tenant');
  const rawRoles = field(raw, 'roles');
  const roles = Array.isArray(rawRoles)
    ? rawRoles.filter((r): r is string => typeof r === 'string')
    : [];
  const rawPerms = field(raw, 'permissions');
  const permissions = Array.isArray(rawPerms)
    ? rawPerms.filter((p): p is string => typeof p === 'string')
    : [];

  if (!user || !tenant || !Array.isArray(rawRoles)) {
    // 不静默：契约被破坏时页面会「能开但少东西」，没有这条日志排查会很久
    console.error('[auth] /auth/me 响应不符合契约，已降级渲染', raw);
  }

  const lastLoginAt = asString(field(user, 'last_login_at'));
  const lastLoginIP = asString(field(user, 'last_login_ip'));
  const hasPassword = asBool(field(user, 'has_password'));
  const roleLabel = asString(field(raw, 'role_label'));
  return {
    user: {
      id: asString(field(user, 'id')) ?? '',
      nickname: asString(field(user, 'nickname')) ?? '',
      phone_masked: asString(field(user, 'phone_masked')) ?? '',
      last_login_at: lastLoginAt ?? null,
      last_login_ip: lastLoginIP ?? null,
      ...(hasPassword === undefined ? {} : { has_password: hasPassword }),
    },
    ...(roleLabel ? { role_label: roleLabel } : {}),
    tenant: {
      id: asString(field(tenant, 'id')) ?? '',
      name: asString(field(tenant, 'name')) ?? '',
      // 两个状态位缺失时一律取「不打扰用户」的一侧：既不误报「企业未认证」
      // 让人白跑一趟交材料，也不凭空把租户当成已停用
      certified: asBool(field(tenant, 'certified')) ?? true,
      suspended: asBool(field(tenant, 'suspended')) ?? false,
    },
    // 角色拿不到就是空数组 = 无任何能力。这是安全侧的默认拒绝方向：
    // 菜单和写操作按钮不渲染，但读接口该给什么仍由服务端说了算
    roles,
    permissions,
  };
}

// 服务端权限点一变（登录、刷新、切租户、登出）就同步给能力判定，
// 让 can() 始终以服务端为真源，不必在每条 set({ me }) 后手动跟一句。
useAuth.subscribe((s) => syncServerPermissions(s.me?.permissions ?? null));

async function fetchMe(): Promise<Me> {
  return normalizeMe(await api.get<unknown>('/auth/me'));
}

/** 落票并拉取当前用户。两条登录路径（验证码 / 密码）共用，避免只改一处。 */
async function adoptSession(
  res: LoginResult,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  writeRefresh(res.refresh_token);
  set({ accessToken: res.access_token });
  const me = await fetchMe();
  set({ me });
}

/* ========== 单飞刷新 ==========
 * 一个页面同时发 8 个请求、8 个都 401 时，只能刷新一次。
 * 并发的其余 7 个必须等同一个 Promise，否则 refresh token 会被轮换机制打成无效。
 */
let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  inFlightRefresh ??= (async () => {
    const refresh = readRefresh();
    if (!refresh) return null;
    try {
      const res = await api.post<LoginResult>('/auth/refresh', {
        body: { refresh_token: refresh },
        refreshOn401: false,
      });
      writeRefresh(res.refresh_token);
      useAuth.setState({ accessToken: res.access_token });
      return res.access_token;
    } catch (e) {
      // 只有明确的鉴权失败才清票；网络抖动不该把用户踢下线
      if (e instanceof ApiError && !e.isTransient) {
        writeRefresh(null);
        useAuth.setState({ accessToken: null, me: null });
      }
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

configureAuth({
  getAccessToken: () => useAuth.getState().accessToken,
  refresh: refreshAccessToken,
  onUnauthorized: (reason) => {
    writeRefresh(null);
    useAuth.setState({
      accessToken: null,
      me: null,
      bootstrapping: false,
      kickReason: reason ?? null,
    });
  },
});
